// Meta's Ads Library only reliably opens a single ad when the URL is JUST
// `https://www.facebook.com/ads/library/?id=<archive_id>` (the format its own
// "Copy ad link" button produces). The ad_snapshot_url our backend/scraper
// sometimes returns instead looks like a *page search* URL (carrying
// active_status/ad_type/country/search_type=page/sort_data params) with the
// ad's archive id merely appended as `&id=...` — Meta then reads that as "find
// a PAGE with this id" rather than "show me this ad", and shows "Ad isn't in
// the ad library" (the exact screenshot this was reported from), even though
// the ad genuinely exists.
//
// Rather than trust whatever shape the backend hands us, pull the archive id
// out of it (wherever it's carried) and always rebuild the canonical
// single-ad URL ourselves.
//
// Deliberately doesn't include a bare "id" here — on these ad records that's
// as likely to be an internal/DB record id as a Facebook archive id, and
// using the wrong one would point at an unrelated (or nonexistent) ad. The
// URL-embedded id (extracted below) is the one actually confirmed to be the
// Facebook archive id, since it's what Meta's own search was matching on.
const ARCHIVE_ID_KEYS = ["ad_archive_id", "archive_id", "fb_ad_id"]

const extractIdFromUrl = url => {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return parsed.searchParams.get("id")
  } catch {
    // Not a fully-qualified URL (or malformed) — fall back to a plain regex
    // over the query string.
    const match = String(url).match(/[?&]id=([^&]+)/)
    return match ? decodeURIComponent(match[1]) : null
  }
}

/**
 * Builds the canonical Meta Ads Library deep link for a single ad, given
 * whatever shape the ad object comes in. Falls back to the ad's landing page
 * if no archive id can be found anywhere.
 */
export const buildAdLibraryUrl = (ad) => {
  if (!ad) return null

  for (const key of ARCHIVE_ID_KEYS) {
    if (ad[key]) return `https://www.facebook.com/ads/library/?id=${encodeURIComponent(ad[key])}`
  }

  const idFromSnapshot = extractIdFromUrl(ad.ad_snapshot_url)
  if (idFromSnapshot) return `https://www.facebook.com/ads/library/?id=${encodeURIComponent(idFromSnapshot)}`

  // No archive id anywhere — the snapshot URL might still be a valid,
  // already-canonical link (or something else entirely); use it as-is before
  // giving up and pointing at the advertiser's own landing page instead.
  return ad.ad_snapshot_url || ad.landing_url || ad.link_url || null
}

/**
 * Fallback for when there's no individual ad to deep-link to at all — e.g.
 * `ad_count > 0` (Meta confirms the advertiser has run ads) but the backend
 * hasn't populated the per-ad creative array (seen in practice when
 * `active_ad_count` is 0: only currently-active ads get full creative
 * details scraped). Rather than show a count with nothing to click, this
 * builds a real Ads Library keyword search for the page/app name — the
 * correct use of `search_type=keyword_unordered`, as opposed to the bug this
 * was all fixed for: bolting an ad's archive id onto that same search-URL
 * shape instead of using the canonical `?id=` link.
 */
export const buildAdLibrarySearchUrl = (pageName) => {
  if (!pageName) return null
  const params = new URLSearchParams({
    active_status: "all",
    ad_type: "all",
    country: "ALL",
    q: pageName,
    search_type: "keyword_unordered",
  })
  return `https://www.facebook.com/ads/library/?${params.toString()}`
}
